import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { getInitials } from 'utils/strings';
import { routeForUserId } from '../../routes/routeConstants';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import { AvatarSize, IAvatarProps } from './AvatarProps';

const SIZE_XSMALL = 22;
const SIZE_SMALL = 33;
const SIZE_MEDIUM = 44;
const SIZE_MED_LARGE = 60;
const SIZE_LARGE = 100;
const SIZE_XLARGE = 121;

interface LayoutProps {
  hasImage?: boolean;
  size?: AvatarSize;
}

const Layout = styled.div<LayoutProps>`
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: ${toRem(33)};
  height: ${toRem(33)};
  border-radius: 50%;
  border: ${({ hasImage }) => (hasImage ? 0 : toRem(1))} solid #ececed;
  background: radial-gradient(100% 180.23% at 100% 0%, ${theme.color.neutral[600]} 1.56%, ${theme.color.neutral[900]} 79.94%, ${theme.color.neutral[700]} 100%);
  overflow: visible;
  font-family: ${theme.font.family.averta};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(11)};
  line-height: 100%;
  text-align: center;
  text-transform: uppercase;
  text-decoration: none;
  color: ${theme.color.white};
  pointer-events: none;

  ${({ size }) =>
    size === 'xsmall' &&
    css`
      width: ${toRem(SIZE_XSMALL)};
      height: ${toRem(SIZE_XSMALL)};
      font-size: ${toRem(8.75)};
      font-weigth: ${theme.font.weight.bold};
    `}

  ${({ size }) =>
    size === 'small' &&
    css`
      width: ${toRem(SIZE_SMALL)};
      height: ${toRem(SIZE_SMALL)};
      font-size: ${toRem(11)};
      font-weigth: ${theme.font.weight.bold};
    `}

  ${({ size }) =>
    size === 'medium' &&
    css`
      width: ${toRem(SIZE_MEDIUM)};
      height: ${toRem(SIZE_MEDIUM)};
      font-size: ${toRem(16.5)};
    `}

  ${({ size }) =>
    size === 'medlarge' &&
    css`
      width: ${toRem(SIZE_MED_LARGE)};
      height: ${toRem(SIZE_MED_LARGE)};
      font-size: ${toRem(16.5)};
    `}

  ${({ size }) =>
    size === 'large' &&
    css`
      width: ${toRem(SIZE_LARGE * 0.66)};
      height: ${toRem(SIZE_LARGE * 0.66)};
      font-size: ${toRem(18.5)};

      @media (min-width: ${theme.breakpoint.small}px) {
        width: ${toRem(SIZE_LARGE * 0.75)};
        height: ${toRem(SIZE_LARGE * 0.75)};
        font-size: ${toRem(25)};
      }

      @media (min-width: ${theme.breakpoint.med}px) {
        width: ${toRem(SIZE_LARGE * 0.88)};
        height: ${toRem(SIZE_LARGE * 0.88)};
        font-size: ${toRem(25)};
      }

      @media (min-width: ${theme.breakpoint.large}px) {
        width: ${toRem(SIZE_LARGE)};
        height: ${toRem(SIZE_LARGE)};
        font-size: ${toRem(27.5)};
      }
    `}

  ${({ size }) =>
    size === 'modal' &&
    css`
      && {
        width: ${toRem(SIZE_LARGE)};
        height: ${toRem(SIZE_LARGE)};
        font-size: ${toRem(27.5)};
      }
    `}

  ${({ size }) =>
    size === 'xlarge' &&
    css`
      width: ${toRem(SIZE_LARGE * 0.66)};
      height: ${toRem(SIZE_LARGE * 0.66)};
      font-size: ${toRem(18.5)};

      @media (min-width: ${theme.breakpoint.small}px) {
        width: ${toRem(SIZE_XLARGE * 0.75)};
        height: ${toRem(SIZE_XLARGE * 0.75)};
        font-size: ${toRem(25)};
      }

      @media (min-width: ${theme.breakpoint.med}px) {
        width: ${toRem(SIZE_XLARGE * 0.88)};
        height: ${toRem(SIZE_XLARGE * 0.88)};
        font-size: ${toRem(33)};
        font-weight: ${theme.font.weight.semiBold};
      }

      @media (min-width: ${theme.breakpoint.large}px) {
        width: ${toRem(SIZE_XLARGE)};
        height: ${toRem(SIZE_XLARGE)};
      }
    `}
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  pointer-events: none;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
`;

interface IndicatorProps {
  size?: AvatarSize;
  bgColor?: string;
  id?: string;
}

const Indicator = styled.span<IndicatorProps>`
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: ${toRem(15)};
  height: ${toRem(15)};
  border-radius: 50%;
  background-color: ${theme.color.success.medium};
  pointer-events: all;

  svg {
    width: ${toRem(7.5)};
    height: ${toRem(7.5)};
  }

  ${({ bgColor }) =>
    bgColor &&
    css`
      background-color: ${bgColor};
    `}

  ${({ size }) =>
    size === 'large' &&
    css`
      width: ${toRem(19.25)};
      height: ${toRem(19.25)};

      svg {
        width: ${toRem(11)};
        height: ${toRem(11)};
      }

      @media (min-width: ${theme.breakpoint.med}px) {
        width: ${toRem(28)};
        height: ${toRem(28)};

        svg {
          width: ${toRem(13.5)};
          height: ${toRem(13.5)};
        }
      }

      @media (min-width: ${theme.breakpoint.large}px) {
        width: ${toRem(33)};
        height: ${toRem(33)};

        svg {
          width: ${toRem(16.5)};
          height: ${toRem(16.5)};
        }
      }
    `}

  ${({ size }) =>
    size === 'modal' &&
    css`
      && {
        width: ${toRem(33)};
        height: ${toRem(33)};

        svg {
          width: ${toRem(16.5)};
          height: ${toRem(16.5)};
        }
      }
    `}

  ${({ size }) =>
    size === 'xlarge' &&
    css`
      width: ${toRem(19.25)};
      height: ${toRem(19.25)};

      svg {
        width: ${toRem(11)};
        height: ${toRem(11)};
      }

      @media (min-width: ${theme.breakpoint.med}px) {
        width: ${toRem(28)};
        height: ${toRem(28)};

        svg {
          width: ${toRem(13.5)};
          height: ${toRem(13.5)};
        }
      }

      @media (min-width: ${theme.breakpoint.large}px) {
        width: ${toRem(33)};
        height: ${toRem(33)};

        svg {
          width: ${toRem(16.5)};
          height: ${toRem(16.5)};
        }
      }
    `}
`;

const PaymentIndicator: React.FC<IndicatorProps> = ({ size, id, ...rest }) => {
  const tooltipId = `payment_${id || 'indicator'}`;
  return (
    <>
      <Indicator size={size} data-tooltip-id={tooltipId} {...rest}>
        <Icon icon="Payment" fill={theme.color.white} />
      </Indicator>

      <Tooltip id={tooltipId} place="right">
        Payment method on file
      </Tooltip>
    </>
  );
};

const DismissedIndicator: React.FC<IndicatorProps> = ({ size, id, ...rest }) => {
  const tooltipId = `dismissed_${id || 'indicator'}`;
  return (
    <>
      <Indicator size={size} data-tooltip-id={tooltipId} bgColor={theme.color.danger.medium} {...rest}>
        <Icon icon="No" fill={theme.color.white} />
      </Indicator>

      <Tooltip id={tooltipId} place="right">
        User is dismissed.
      </Tooltip>
    </>
  );
};

const StyledSkeleton = styled(Skeleton)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50% !important;
`;

const Link = styled.a`
  text-decoration: none;
  color: ${theme.color.neutral[800]};

  &:active,
  &:visited {
    text-decoration: none;
    color: ${theme.color.neutral[800]};
  }
`;

const Avatar: React.FC<IAvatarProps> = ({ userId, username, image, size = 'medium', hasPaymentMethod, isDismissed, isLoading, className, hasLink, id, ...rest }) => {
  const avatarBaseUrl = process.env.REACT_APP_AVATAR_URL;
  const [showImage, setShowImage] = useState<boolean>(true);
  const avatarUrl = !image && userId ? `${avatarBaseUrl}avatar_${userId}?${Date.now()}` : image;

  const content = (
    <Layout className={className} size={size} hasImage={Boolean(avatarUrl && showImage)} {...rest}>
      {isLoading ? <StyledSkeleton circle /> : avatarUrl && showImage ? <Image src={avatarUrl} alt={username} onError={() => setShowImage(false)} /> : getInitials(username, 2)}
      {!isLoading && isDismissed && <DismissedIndicator data-cy="dismissed-indicator" size={size} id={id || userId} />}
      {!isLoading && !isDismissed && hasPaymentMethod && <PaymentIndicator data-cy="payment-indicator" size={size} id={id || userId} />}
    </Layout>
  );
  return hasLink && id ? (
    <Link data-cy="avatar-link" href={routeForUserId(id)}>
      {content}
    </Link>
  ) : (
    content
  );
};

export default Avatar;
